import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect } from "react";
import ReactGA from "react-ga4";
import { useCurrentUser } from "src/modules/User";
import LoginErrorPage from "src/pages/Auth0Error";
import { MediaContextProvider } from "./Media";
import Routes from "./Routes";
import { FullStory } from "@fullstory/browser";
import "./App.css";
import { useGetCurrentPrimacyAgencyQuery } from "src/services";

if (process.env.REACT_APP_GOOGLE_ANALYTICS_ID) {
  ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_ID ?? "");
}

const App: React.FC = () => {
  const {
    error: authError,
    isAuthenticated,
    isLoading: isAuthLoading,
    loginWithRedirect,
  } = useAuth0();
  const { isLoading: isUserLoading, error: userLoadError, user } = useCurrentUser();

  const { data: primacyAgency } = useGetCurrentPrimacyAgencyQuery(undefined);

  useEffect(() => {
    if (process.env.REACT_APP_FULLSTORY_ID && user) {
      FullStory("setIdentity", {
        uid: user?.id.toString(),
        properties: {
          displayName: user.firstName + " " + user.lastName,
          email: user.email,
          application: "State Dashboard",
          auth0Id: user.authId,
          accountCreationDate: primacyAgency?.createdOn,
          userCreationDate: user.createdOn,
          accountId: primacyAgency?.accountId,
          accountName: primacyAgency?.name,
        },
      });
    }
  }, [user, primacyAgency]);

  if ((!isAuthenticated && !isAuthLoading && authError != null) || (!isUserLoading && userLoadError != null)) {
    // Something went wrong with authorizing with Auth0.
    // We need to drop the user on a page where they can see the error and try again.
    return <LoginErrorPage />;
  }

  if (!isAuthLoading && !isAuthenticated) {
    void loginWithRedirect({ authorizationParams: { audience: "StateDashboard" } });
  }

  // We show the loading screen if we're still fetching any part of the user or
  // if Auth0 isn't authenticated.
  const isLoading = isAuthLoading || isUserLoading || !isAuthenticated;
  return (
    <MediaContextProvider>
      <Routes isLoading={isLoading} />
    </MediaContextProvider>
  );
};

export default App;
